import { FC, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useIntl } from "react-intl";

import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import {
  ANOMALY_COLORS,
  ANOMALY_L2_BORDER_COLOR,
  ANOMALY_TIMELINE_BACKGROUND,
  ANOMALY_WARNING_THRESHOLD,
  TIMELINE_ACTIVE_MARKER_RADIUS,
  TIMELINE_BAR_Y,
  TIMELINE_STRIPE_WIDTH,
  TIMELINE_TOOLTIP_MAX_WIDTH,
  TIMELINE_TOOLTIP_MIN_WIDTH,
  TIMELINE_TOOLTIP_VIEWPORT_PADDING,
  TIMELINE_WIDTH,
} from "./constants";
import { IAnomalyItem, TTooltipPosition } from "./interfaces";
import { buildAnomalyTimelineModel, calculateAnomalyTooltipPosition } from "./utils";
import { useFormatDateHumanReadable } from "../../../../../../hooks";

interface IAnomalyTimelineProps {
  items: IAnomalyItem[];
  startTimestamp: string | Date;
  endTimestamp: string | Date;
}

export const AnomalyTimeline: FC<IAnomalyTimelineProps> = props => {
  const { items, startTimestamp, endTimestamp } = props;
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const formatDate = useFormatDateHumanReadable();
  const tooltipId = useId();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [hoveredStripeKey, setHoveredStripeKey] = useState<string | null>(null);
  const [focusedStripeKey, setFocusedStripeKey] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<TTooltipPosition | null>(null);

  const timeline = useMemo(
    () => buildAnomalyTimelineModel(items, startTimestamp, endTimestamp),
    [endTimestamp, items, startTimestamp],
  );

  const activeStripe = useMemo(() => {
    const hoveredStripe = timeline.stripes.find(stripe => stripe.key === hoveredStripeKey);

    return hoveredStripe ?? timeline.stripes.find(stripe => stripe.key === focusedStripeKey) ?? null;
  }, [focusedStripeKey, hoveredStripeKey, timeline.stripes]);

  useEffect(() => {
    if (hoveredStripeKey && !timeline.stripes.some(stripe => stripe.key === hoveredStripeKey)) {
      setHoveredStripeKey(null);
    }

    if (focusedStripeKey && !timeline.stripes.some(stripe => stripe.key === focusedStripeKey)) {
      setFocusedStripeKey(null);
    }
  }, [focusedStripeKey, hoveredStripeKey, timeline.stripes]);

  useLayoutEffect(() => {
    if (!activeStripe || !svgRef.current || !tooltipRef.current) {
      setTooltipPosition(null);
      return;
    }

    const svgNode = svgRef.current;
    const tooltipNode = tooltipRef.current;

    const updatePosition = () => {
      const tooltipRect = tooltipNode.getBoundingClientRect();
      const nextPosition = calculateAnomalyTooltipPosition({
        activeStripe,
        svgRect: svgNode.getBoundingClientRect(),
        tooltipSize: { width: tooltipRect.width, height: tooltipRect.height },
        timelineHeight: timeline.height,
        viewportSize: { width: window.innerWidth, height: window.innerHeight },
      });

      setTooltipPosition(currentPosition =>
        currentPosition?.left === nextPosition.left && currentPosition.top === nextPosition.top
          ? currentPosition
          : nextPosition,
      );
    };

    updatePosition();

    let animationFrame: number | null = null;
    const schedulePositionUpdate = () => {
      if (animationFrame !== null) {
        return;
      }

      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = null;
        updatePosition();
      });
    };

    window.addEventListener("resize", schedulePositionUpdate);
    window.addEventListener("scroll", schedulePositionUpdate, true);

    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedulePositionUpdate) : null;

    resizeObserver?.observe(svgNode);
    resizeObserver?.observe(tooltipNode);

    return () => {
      window.removeEventListener("resize", schedulePositionUpdate);
      window.removeEventListener("scroll", schedulePositionUpdate, true);
      resizeObserver?.disconnect();

      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }
    };
  }, [activeStripe, timeline.height]);

  return (
    <Box sx={{ width: "100%", mt: 1 }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${TIMELINE_WIDTH} ${timeline.height}`}
        style={{ width: "100%", height: "auto", overflow: "visible", display: "block" }}
      >
        <rect
          x={timeline.barStart}
          y={TIMELINE_BAR_Y}
          width={timeline.barWidth}
          height={timeline.barHeight}
          fill={ANOMALY_TIMELINE_BACKGROUND}
          stroke="#1c1c1c"
          strokeWidth="1"
        />
        {timeline.stripes.map(stripe => (
          <g key={stripe.key}>
            <rect
              x={stripe.x}
              y={stripe.y}
              width={TIMELINE_STRIPE_WIDTH}
              height={stripe.height}
              fill={stripe.color}
              opacity={stripe.Value <= ANOMALY_WARNING_THRESHOLD ? 0.95 : 1}
              stroke={stripe.key === activeStripe?.key ? "#ffffff" : "none"}
              strokeWidth={stripe.key === activeStripe?.key ? 1.5 : 0}
              style={{ cursor: "pointer", outline: "none" }}
              tabIndex={0}
              aria-label={formatDate(stripe.Date)}
              aria-describedby={stripe.key === activeStripe?.key ? tooltipId : undefined}
              onMouseEnter={() => setHoveredStripeKey(stripe.key)}
              onMouseLeave={() => setHoveredStripeKey(current => (current === stripe.key ? null : current))}
              onFocus={() => setFocusedStripeKey(stripe.key)}
              onBlur={() => setFocusedStripeKey(current => (current === stripe.key ? null : current))}
            />
          </g>
        ))}
        {activeStripe ? (
          <circle
            cx={activeStripe.x + TIMELINE_STRIPE_WIDTH / 2}
            cy={activeStripe.y + activeStripe.height / 2}
            r={TIMELINE_ACTIVE_MARKER_RADIUS}
            fill="#ffffff"
            stroke={activeStripe.color}
            strokeWidth="2"
            pointerEvents="none"
          />
        ) : null}
        {timeline.ticks.map(tick => (
          <g key={tick.key}>
            <line
              x1={tick.x}
              y1={timeline.axisY}
              x2={tick.x}
              y2={timeline.axisY + 8}
              stroke="#1c1c1c"
              strokeWidth="1"
            />
            <text
              x={tick.x}
              y={timeline.labelY}
              fill="#363846"
              textAnchor="middle"
              style={{
                fontFamily: theme.typography.fontFamily,
                fontSize: "13px",
                fontWeight: 500,
              }}
            >
              {tick.label}
            </text>
          </g>
        ))}
      </svg>
      {activeStripe
        ? createPortal(
            <Box
              id={tooltipId}
              ref={tooltipRef}
              role="tooltip"
              sx={{
                position: "fixed",
                left: tooltipPosition?.left ?? 0,
                top: tooltipPosition?.top ?? 0,
                minWidth: `min(${TIMELINE_TOOLTIP_MIN_WIDTH}px, calc(100vw - ${
                  TIMELINE_TOOLTIP_VIEWPORT_PADDING * 2
                }px))`,
                maxWidth: `min(${TIMELINE_TOOLTIP_MAX_WIDTH}px, calc(100vw - ${
                  TIMELINE_TOOLTIP_VIEWPORT_PADDING * 2
                }px))`,
                px: 1.25,
                py: 1,
                borderRadius: "6px",
                backgroundColor: "rgba(0, 0, 0, 0.72)",
                color: "#ffffff",
                boxShadow: "0 12px 28px rgba(0, 0, 0, 0.18)",
                backdropFilter: "blur(4px)",
                pointerEvents: "none",
                zIndex: theme.zIndex.tooltip,
                visibility: tooltipPosition ? "visible" : "hidden",
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  lineHeight: "12px",
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: "rgba(255, 255, 255, 0.6)",
                }}
              >
                {formatMessage({ id: "pages.scheme.stateAndForecast.anomalyTooltip.date" })}
              </Typography>
              <Typography sx={{ fontSize: 13, lineHeight: "18px", fontWeight: 700, color: "#ffffff", mb: 0.5 }}>
                {formatDate(activeStripe.Date)}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.25 }}>
                <Typography sx={{ fontSize: 11, lineHeight: "14px", color: "rgba(255, 255, 255, 0.72)" }}>
                  {formatMessage({ id: "pages.scheme.stateAndForecast.anomalyTooltip.level" })}
                </Typography>
                <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
                  <Box
                    role="img"
                    aria-label={activeStripe.level.toUpperCase()}
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: activeStripe.color,
                      border:
                        activeStripe.color === ANOMALY_COLORS.l2 ? `1px solid ${ANOMALY_L2_BORDER_COLOR}` : "none",
                    }}
                  />
                </Box>
              </Box>
            </Box>,
            document.body,
          )
        : null}
    </Box>
  );
};
